import { Fragment } from 'react';
import { generatePath } from 'react-router-dom';
import { uniqBy } from 'lodash-es';
import type { DataTableColumnDef } from 'components/data-table';
import { Link, PlainLink } from 'components/link';
import { EMPTY } from 'components/vesting-documents/types';
import routes from '~/routes';
import styles from './party-links.module.scss';

interface PartyLinkFields {
  entities: { id: number; name: string }[];
  accountName: string | null;
  /** Salesforce: a CRM account has no Insights page. */
  accountHref: string | null;
}

// One span: a table cell lays out its children as flex items, which spaces out the commas.
export const LinkList = ({
  links,
}: {
  links: { key: string | number; to: string; label: string }[];
}) => {
  if (!links.length) return <>{EMPTY}</>;
  return (
    <span
      className={styles.links}
      title={links.map((link) => link.label).join(', ')}
    >
      {links.map((link, index) => (
        <Fragment key={link.key}>
          {index > 0 && ', '}
          <Link to={link.to}>{link.label}</Link>
        </Fragment>
      ))}
    </span>
  );
};

export const EntityLinks = ({
  entities,
}: Pick<PartyLinkFields, 'entities'>) => (
  <LinkList
    links={uniqBy(entities, 'id').map((entity) => ({
      key: entity.id,
      to: generatePath(routes.entityPath, { id: entity.id }),
      label: entity.name,
    }))}
  />
);

export const withPartyLinks = <Row extends PartyLinkFields>(
  columns: DataTableColumnDef<Row>[]
): DataTableColumnDef<Row>[] =>
  columns.map((column) => {
    if (column.id === 'entityName')
      return {
        ...column,
        meta: { ...column.meta, wrap: true },
        cell: ({ row }) => <EntityLinks entities={row.original.entities} />,
      } as DataTableColumnDef<Row>;
    if (column.id === 'accountName')
      return {
        ...column,
        cell: ({ row }) => {
          const { accountName, accountHref } = row.original;
          if (!accountName) return EMPTY;
          if (!accountHref) return accountName;
          return (
            <PlainLink href={accountHref} openInNewTab>
              {accountName}
            </PlainLink>
          );
        },
      } as DataTableColumnDef<Row>;
    return column;
  });
